/**
 * EmailAccountsManager — CRUD für mehrere Mail-Konten pro Tenant.
 *
 * Jeder User kann ein Konto für sich anlegen. Beim Versand wählt das Backend
 * automatisch das Konto dessen from_email = Login-Email — Fallback: Default-Konto.
 */
import React, { useEffect, useState } from 'react';
import { apiClient } from '../../api/client';

export interface EmailAccount {
  id: string;
  label: string;
  from_email: string;
  from_name: string | null;
  smtp_host: string;
  smtp_port: number;
  smtp_user: string;
  smtp_password: string | null;   // 'set' oder null
  imap_host: string | null;
  imap_port: number;
  imap_user: string | null;
  imap_password: string | null;   // 'set' oder null
  is_default: boolean;
  is_active: boolean;
  last_sync_at: string | null;
  last_sync_status: string | null;
}

interface FormState {
  label: string;
  from_email: string;
  from_name: string;
  smtp_host: string;
  smtp_port: string;
  smtp_user: string;
  smtp_password: string;
  imap_host: string;
  imap_port: string;
  imap_user: string;
  imap_password: string;
  is_default: boolean;
  is_active: boolean;
}

const emptyForm: FormState = {
  label: '', from_email: '', from_name: '',
  smtp_host: '', smtp_port: '587', smtp_user: '', smtp_password: '',
  imap_host: '', imap_port: '993', imap_user: '', imap_password: '',
  is_default: false, is_active: true,
};

export function EmailAccountsManager() {
  const [accounts, setAccounts] = useState<EmailAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<{ id: string | null; form: FormState } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get<{ data: EmailAccount[] }>('/email-accounts');
      setAccounts(data.data);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  const startNew = () => setEditing({ id: null, form: { ...emptyForm } });

  const startEdit = (a: EmailAccount) => setEditing({
    id: a.id,
    form: {
      label: a.label,
      from_email: a.from_email,
      from_name: a.from_name ?? '',
      smtp_host: a.smtp_host,
      smtp_port: String(a.smtp_port),
      smtp_user: a.smtp_user,
      smtp_password: '',   // leer = unverändert lassen
      imap_host: a.imap_host ?? '',
      imap_port: String(a.imap_port),
      imap_user: a.imap_user ?? '',
      imap_password: '',
      is_default: a.is_default,
      is_active: a.is_active,
    },
  });

  const cancel = () => { setEditing(null); setError(null); };

  const save = async () => {
    if (!editing) return;
    setBusy(true); setError(null);
    try {
      const payload: Record<string, unknown> = {
        label: editing.form.label,
        from_email: editing.form.from_email,
        from_name: editing.form.from_name || null,
        smtp_host: editing.form.smtp_host,
        smtp_port: parseInt(editing.form.smtp_port, 10),
        smtp_user: editing.form.smtp_user,
        imap_host: editing.form.imap_host || null,
        imap_port: parseInt(editing.form.imap_port, 10),
        imap_user: editing.form.imap_user || null,
        is_default: editing.form.is_default,
        is_active: editing.form.is_active,
      };
      if (editing.form.smtp_password) payload.smtp_password = editing.form.smtp_password;
      if (editing.form.imap_password) payload.imap_password = editing.form.imap_password;

      if (editing.id) {
        await apiClient.put(`/email-accounts/${editing.id}`, payload);
      } else {
        if (!editing.form.smtp_password) {
          setError('SMTP-Passwort ist bei Neuanlage Pflicht.');
          setBusy(false);
          return;
        }
        await apiClient.post('/email-accounts', payload);
      }
      await load();
      setEditing(null);
    } catch (e) {
      const msg = (e as { response?: { data?: { error?: string } } }).response?.data?.error;
      setError(msg ?? (e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const del = async (id: string, label: string) => {
    if (!confirm(`Mail-Konto "${label}" wirklich löschen?`)) return;
    setBusy(true);
    try {
      await apiClient.delete(`/email-accounts/${id}`);
      await load();
    } catch (e) {
      setError((e as Error).message);
    } finally { setBusy(false); }
  };

  const setDefault = async (id: string) => {
    setBusy(true);
    try {
      await apiClient.post(`/email-accounts/${id}/set-default`, {});
      await load();
    } catch (e) {
      setError((e as Error).message);
    } finally { setBusy(false); }
  };

  const field = (label: string, value: string, onChange: (v: string) => void, opts: { type?: string; placeholder?: string; required?: boolean } = {}) => (
    <div>
      <label style={{ display: 'block', fontSize: 11, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text3)', marginBottom: 4 }}>
        {label}{opts.required && <span style={{ color: '#c62828' }}> *</span>}
      </label>
      <input
        type={opts.type ?? 'text'}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={opts.placeholder}
        style={{
          width: '100%', padding: '8px 12px', border: '1px solid var(--border2)',
          borderRadius: 'var(--radius)', fontSize: 13, background: 'var(--surface)', color: 'var(--text)',
        }}
      />
    </div>
  );

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)' }}>Mail-Konten</div>
          <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>
            SMTP-/IMAP-Konten. Der Versand wählt automatisch das Konto, dessen Absenderadresse zur Login-E-Mail passt (Fallback: Default-Konto). Antworten werden aus <strong>allen</strong> aktiven Konten synchronisiert und sind für alle User sichtbar.
          </div>
        </div>
        <button
          type="button"
          onClick={startNew}
          disabled={!!editing}
          style={{
            padding: '8px 14px', fontSize: 13, fontWeight: 600,
            background: editing ? 'var(--surface2)' : 'var(--accent)',
            color: editing ? 'var(--text3)' : '#fff',
            border: 'none', borderRadius: 'var(--radius)',
            cursor: editing ? 'not-allowed' : 'pointer',
          }}
        >
          + Neues Konto
        </button>
      </div>

      {loading && <div style={{ padding: 24, textAlign: 'center', color: 'var(--text3)' }}>Lädt…</div>}

      {!loading && accounts.length === 0 && !editing && (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--text3)', background: 'var(--surface2)', borderRadius: 'var(--radius)' }}>
          Noch keine Mail-Konten angelegt. Lege dein erstes an.
        </div>
      )}

      {!loading && accounts.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: '2px solid var(--border)', textAlign: 'left', background: 'var(--surface2)' }}>
              <th style={{ padding: 8, fontSize: 11, textTransform: 'uppercase', color: 'var(--text3)' }}>Label</th>
              <th style={{ padding: 8, fontSize: 11, textTransform: 'uppercase', color: 'var(--text3)' }}>Absender</th>
              <th style={{ padding: 8, fontSize: 11, textTransform: 'uppercase', color: 'var(--text3)' }}>SMTP-Host</th>
              <th style={{ padding: 8, fontSize: 11, textTransform: 'uppercase', color: 'var(--text3)', textAlign: 'center' }}>Default</th>
              <th style={{ padding: 8, fontSize: 11, textTransform: 'uppercase', color: 'var(--text3)', textAlign: 'center' }}>Aktiv</th>
              <th style={{ padding: 8, fontSize: 11, textTransform: 'uppercase', color: 'var(--text3)' }}>Letzter Sync</th>
              <th style={{ padding: 8, fontSize: 11, textTransform: 'uppercase', color: 'var(--text3)', textAlign: 'right' }}>Aktion</th>
            </tr>
          </thead>
          <tbody>
            {accounts.map(a => (
              <tr key={a.id} style={{ borderBottom: '1px solid var(--border)' }}>
                <td style={{ padding: 8, fontWeight: 500 }}>{a.label}</td>
                <td style={{ padding: 8, fontFamily: 'DM Mono, monospace', fontSize: 12 }}>{a.from_email}</td>
                <td style={{ padding: 8, fontFamily: 'DM Mono, monospace', fontSize: 12 }}>{a.smtp_host}:{a.smtp_port}</td>
                <td style={{ padding: 8, textAlign: 'center' }}>
                  {a.is_default ? (
                    <span style={{ padding: '2px 8px', background: '#e8f5e9', color: '#2e7d32', borderRadius: 12, fontSize: 11, fontWeight: 600 }}>✓ Default</span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => void setDefault(a.id)}
                      disabled={busy}
                      style={{ padding: '2px 8px', fontSize: 11, background: 'var(--surface)', border: '1px solid var(--border2)', borderRadius: 12, cursor: 'pointer', color: 'var(--text3)' }}
                    >
                      als Default
                    </button>
                  )}
                </td>
                <td style={{ padding: 8, textAlign: 'center' }}>
                  <span style={{
                    padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600,
                    background: a.is_active ? '#e8f5e9' : '#fff3e0',
                    color: a.is_active ? '#2e7d32' : '#e65100',
                  }}>
                    {a.is_active ? '✓ aktiv' : '⏸ inaktiv'}
                  </span>
                </td>
                <td style={{ padding: 8, fontSize: 11, color: 'var(--text3)' }}>
                  {a.last_sync_at ? new Date(a.last_sync_at).toLocaleString('de-DE') : '—'}
                  {a.last_sync_status && (
                    <div style={{ fontSize: 10, color: a.last_sync_status === 'ok' ? '#2e7d32' : '#c62828', marginTop: 2 }}>
                      {a.last_sync_status.slice(0, 60)}
                    </div>
                  )}
                </td>
                <td style={{ padding: 8, textAlign: 'right', whiteSpace: 'nowrap' }}>
                  <button
                    type="button"
                    onClick={() => startEdit(a)}
                    disabled={!!editing || busy}
                    style={{ padding: '4px 10px', fontSize: 12, background: 'var(--surface)', border: '1px solid var(--border2)', borderRadius: 4, cursor: 'pointer', marginRight: 4 }}
                  >
                    Bearbeiten
                  </button>
                  <button
                    type="button"
                    onClick={() => void del(a.id, a.label)}
                    disabled={busy}
                    style={{ padding: '4px 10px', fontSize: 12, background: '#fff', color: '#c62828', border: '1px solid #c62828', borderRadius: 4, cursor: 'pointer' }}
                  >
                    Löschen
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {editing && (
        <div style={{ marginTop: 20, padding: 16, background: 'var(--surface2)', borderRadius: 'var(--radius)', border: '1px solid var(--border)' }}>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>
            {editing.id ? 'Mail-Konto bearbeiten' : 'Neues Mail-Konto'}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            {field('Label (intern)', editing.form.label, v => setEditing({ ...editing, form: { ...editing.form, label: v } }), { placeholder: 'Michael Rubin', required: true })}
            {field('Absender-Name', editing.form.from_name, v => setEditing({ ...editing, form: { ...editing.form, from_name: v } }), { placeholder: 'Michael Rubin, Rubin Real Estate' })}
            {field('Absender-Email', editing.form.from_email, v => setEditing({ ...editing, form: { ...editing.form, from_email: v } }), { type: 'email', placeholder: 'michael@rubin.estate', required: true })}
            {field(editing.id ? 'SMTP-Passwort (leer = unverändert)' : 'SMTP-Passwort', editing.form.smtp_password, v => setEditing({ ...editing, form: { ...editing.form, smtp_password: v } }), { type: 'password', required: !editing.id })}
          </div>

          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', marginBottom: 8, marginTop: 4 }}>SMTP (Versand)</div>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 2fr', gap: 12, marginBottom: 12 }}>
            {field('SMTP-Host', editing.form.smtp_host, v => setEditing({ ...editing, form: { ...editing.form, smtp_host: v } }), { placeholder: 'smtp.ionos.de', required: true })}
            {field('Port', editing.form.smtp_port, v => setEditing({ ...editing, form: { ...editing.form, smtp_port: v } }), { type: 'number' })}
            {field('SMTP-Benutzer', editing.form.smtp_user, v => setEditing({ ...editing, form: { ...editing.form, smtp_user: v } }), { placeholder: 'michael@rubin.estate', required: true })}
          </div>

          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', marginBottom: 8, marginTop: 4 }}>IMAP (Antworten synchronisieren, optional)</div>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 2fr', gap: 12, marginBottom: 12 }}>
            {field('IMAP-Host', editing.form.imap_host, v => setEditing({ ...editing, form: { ...editing.form, imap_host: v } }), { placeholder: 'imap.ionos.de (leer = auto)' })}
            {field('Port', editing.form.imap_port, v => setEditing({ ...editing, form: { ...editing.form, imap_port: v } }), { type: 'number' })}
            {field('IMAP-Benutzer', editing.form.imap_user, v => setEditing({ ...editing, form: { ...editing.form, imap_user: v } }), { placeholder: 'leer = SMTP-User' })}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 12, marginBottom: 12 }}>
            {field(editing.id ? 'IMAP-Passwort (leer = SMTP-Passwort verwenden)' : 'IMAP-Passwort (leer = SMTP-Passwort verwenden)', editing.form.imap_password, v => setEditing({ ...editing, form: { ...editing.form, imap_password: v } }), { type: 'password' })}
          </div>

          <div style={{ display: 'flex', gap: 16, marginBottom: 12 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
              <input type="checkbox" checked={editing.form.is_default} onChange={e => setEditing({ ...editing, form: { ...editing.form, is_default: e.target.checked } })} />
              Als Default-Konto setzen
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
              <input type="checkbox" checked={editing.form.is_active} onChange={e => setEditing({ ...editing, form: { ...editing.form, is_active: e.target.checked } })} />
              Aktiv (IMAP-Sync + Versand)
            </label>
          </div>

          {error && (
            <div style={{ padding: '8px 12px', background: '#ffebee', color: '#c62828', borderRadius: 'var(--radius)', fontSize: 12, marginBottom: 12 }}>
              ⚠️ {error}
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={cancel}
              disabled={busy}
              style={{ padding: '8px 16px', fontSize: 13, background: 'var(--surface)', border: '1px solid var(--border2)', borderRadius: 'var(--radius)', cursor: 'pointer' }}
            >
              Abbrechen
            </button>
            <button
              type="button"
              onClick={() => void save()}
              disabled={busy}
              style={{ padding: '8px 18px', fontSize: 13, fontWeight: 600, background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 'var(--radius)', cursor: busy ? 'wait' : 'pointer' }}
            >
              {busy ? '⏳ Speichert…' : editing.id ? 'Speichern' : 'Konto anlegen'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
